import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { RootState } from "../index";
import { adminApi } from "../../repositories/admin";
import Swal from "sweetalert2";

export interface CustomPackage {
  id: string;
  package_id: string;
  plan_name: string;
  amount: string;
  interval: string;
  permissions?: Array<{
    limit: number | null;
    permission_id: number;
    permission_name: string;
  }>;
  status?: number;
  createdAt?: string;
  updatedAt?: string;
}

interface CustomPackageState {
  packages: CustomPackage[];
  loading: boolean;
  error: string | null;
}

const initialState: CustomPackageState = {
  packages: [],
  loading: false,
  error: null,
};

// Async thunk for fetching custom packages
export const fetchCustomPackages = createAsyncThunk(
  "customPackage/fetchCustomPackages",
  async () => {
    try {
      const response = await adminApi.getAllCustomPackages();
      return response.data.data || [];
    } catch (error: any) {
      if (error.response?.status === 404) {
        return [];
      }
      Swal.fire(
        "Error",
        error?.response?.data?.message ||
        error.message ||
        "An unknown error occurred while fetching custom packages",
        "error"
      );
      throw error;
    }
  }
);

export const customPackageSlice = createSlice({
  name: "customPackage",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCustomPackages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomPackages.fulfilled, (state, action) => {
        state.loading = false;
        state.packages = action.payload;
      })
      .addCase(fetchCustomPackages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch custom packages";
      });
  },
});

export const { clearError } = customPackageSlice.actions;

export const selectCustomPackages = (state: RootState) => state.customPackage.packages;
export const selectCustomPackagesLoading = (state: RootState) => state.customPackage.loading;
export const selectCustomPackagesError = (state: RootState) => state.customPackage.error;

export default customPackageSlice.reducer;
